import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { BackButton } from '../components/BackButton.js';
import { vatLabel } from '../components/Invoice.js';
import { PageTitle } from '../components/Layouts.js';
import { formatMoney } from '../components/site.js';
import {
  Button,
  Card,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SelectField,
  TextField,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { intlLocale } from '../i18n.js';

type PaidPlan = 'starter' | 'business' | 'agency';
type Interval = 'month' | 'year';
type Address = Schemas['StoredAddress'];
type Method = 'transfer' | 'online';

/** The quote asks VIES only once typing has paused (T-63). */
function useSettled<T>(value: T, ms = 600): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

const go = (url: string) => {
  window.location.assign(url);
};

const isPlan = (v: string | null): v is PaidPlan =>
  v === 'starter' || v === 'business' || v === 'agency';

function AddressForm({
  initial,
  countries,
  plan,
  interval,
  methods,
}: {
  initial: Address;
  countries: string[];
  plan: PaidPlan;
  interval: Interval;
  methods: Method[];
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [address, setAddress] = useState<Address>(initial);
  const [method, setMethod] = useState<Method>('transfer');
  const vatId = useSettled(address.vatId?.trim() ?? '');
  const set = (key: keyof Address) => (e: { target: { value: string } }) =>
    setAddress({ ...address, [key]: e.target.value });
  const names = new Intl.DisplayNames([intlLocale(i18n.language)], { type: 'region' });
  const quote = useQuery({
    queryKey: ['billing', 'quote', plan, interval, address.country, vatId],
    queryFn: () =>
      unwrap(
        api.GET('/billing/quote', {
          params: {
            query: {
              planCode: plan,
              interval,
              country: address.country,
              ...(vatId ? { vatId } : {}),
            },
          },
        }),
      ),
    placeholderData: (previous) => previous,
  });
  const body = { planCode: plan, interval, address: { ...address, vatId: address.vatId ?? '' } };
  const order = useMutation({
    mutationFn: async () =>
      method === 'online'
        ? // Online: the same checked address and VAT go to Stripe, which takes the payment.
          { url: (await unwrap(api.POST('/billing/checkout-session', { body }))).url }
        : { invoiceId: (await unwrap(api.POST('/billing/invoices', { body }))).id },
    onSuccess: (result) =>
      'url' in result
        ? go(result.url)
        : void navigate(`/billing/invoices/${result.invoiceId}?ordered=1`),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    order.mutate();
  };
  const openId =
    order.error instanceof ApiError && order.error.code === 'CONFLICT'
      ? (order.error.problem as { invoiceId?: string }).invoiceId
      : undefined;
  const q = quote.data;
  const money = (cents: number) => formatMoney(cents, i18n.language);

  return (
    <form onSubmit={submit} className="grid gap-4 lg:grid-cols-[1fr_22rem] lg:items-start">
      <Card>
        <h2 className="mb-3 text-xl font-semibold">{t('checkout.address')}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <TextField
              label={t('checkout.name')}
              hint={t('checkout.nameHint')}
              required
              minLength={2}
              maxLength={200}
              autoComplete="organization"
              value={address.name}
              onChange={set('name')}
            />
          </div>
          <div className="sm:col-span-2">
            <TextField
              label={t('checkout.street')}
              required
              minLength={3}
              maxLength={200}
              autoComplete="street-address"
              value={address.street}
              onChange={set('street')}
            />
          </div>
          <TextField
            label={t('checkout.postalCode')}
            required
            minLength={3}
            maxLength={16}
            autoComplete="postal-code"
            value={address.postalCode}
            onChange={set('postalCode')}
          />
          <TextField
            label={t('checkout.city')}
            required
            minLength={2}
            maxLength={100}
            autoComplete="address-level2"
            value={address.city}
            onChange={set('city')}
          />
          <SelectField
            label={t('checkout.country')}
            value={address.country}
            onChange={set('country')}
          >
            {countries.map((c) => (
              <option key={c} value={c}>
                {names.of(c) ?? c}
              </option>
            ))}
          </SelectField>
          <TextField
            label={t('checkout.vatId')}
            hint={t('checkout.vatIdHint')}
            optional
            maxLength={20}
            value={address.vatId ?? ''}
            onChange={set('vatId')}
            error={
              order.error instanceof ApiError &&
              ['VALIDATION_FAILED', 'VAT_ID_INVALID'].includes(order.error.code)
                ? t('checkout.vatIdInvalid')
                : q?.vatIdStatus === 'invalid' || q?.vatIdStatus === 'mismatch'
                  ? t(`checkout.vatIdStatus.${q.vatIdStatus}`)
                  : undefined
            }
          />
          {q?.vatIdStatus === 'valid' || q?.vatIdStatus === 'unavailable' ? (
            <p
              className={`text-sm sm:col-span-2 ${q.vatIdStatus === 'valid' ? 'text-green-800' : 'text-amber-900'}`}
              role="status"
            >
              {q.vatIdStatus === 'valid'
                ? t('checkout.vatIdStatus.valid', { name: q.vatIdName ?? '' })
                : t('checkout.vatIdStatus.unavailable')}
            </p>
          ) : null}
        </div>
        <fieldset className="mt-5">
          <legend className="mb-2 font-semibold">{t('checkout.method')}</legend>
          <div className="flex flex-col gap-2">
            {(['transfer', 'online'] as const).map((m) => {
              const available = methods.includes(m);
              const chosen = method === m;
              return (
                <label
                  key={m}
                  className={`flex items-start gap-3 rounded-md p-3 ${chosen ? 'border-2 border-blue-700 bg-blue-50' : 'border border-slate-200'} ${available ? '' : 'text-slate-600'}`}
                >
                  <input
                    type="radio"
                    name="method"
                    value={m}
                    checked={chosen}
                    disabled={!available}
                    onChange={() => setMethod(m)}
                    className="mt-1 h-5 w-5 accent-blue-700"
                  />
                  <span>
                    <strong>{t(m === 'transfer' ? 'checkout.transfer' : 'checkout.card')}</strong>
                    <span className="block text-sm text-slate-800">
                      {m === 'transfer'
                        ? t('checkout.transferText')
                        : available
                          ? t('checkout.cardText')
                          : t('checkout.cardSoon')}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      </Card>

      <Card className="lg:sticky lg:top-4">
        <h2 className="mb-3 text-xl font-semibold">{t('checkout.summary')}</h2>
        <p className="text-lg font-bold">
          {t(`admin.plans.${plan}`)} · {t(`billing.intervals.${interval}`)}
        </p>
        {q ? (
          <dl className="mt-3 flex flex-col gap-1 tabular-nums">
            <div className="flex justify-between gap-2">
              <dt>{t('invoice.net')}</dt>
              <dd>{money(q.netCents)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>{t('invoice.vat', { rate: vatLabel(q.vatRateBp) })}</dt>
              <dd>{money(q.vatCents)}</dd>
            </div>
            <div className="flex justify-between gap-2 border-t border-slate-300 pt-2 text-lg font-bold">
              <dt>{t('invoice.gross')}</dt>
              <dd>{money(q.grossCents)}</dd>
            </div>
          </dl>
        ) : (
          <Loading />
        )}
        {q?.vatNote ? (
          <p className="mt-2 text-sm text-slate-700">{t(`invoice.vatNote.${q.vatNote}`)}</p>
        ) : null}
        <p className="mt-3 text-sm text-slate-800">
          {method === 'online' ? t('checkout.renewsOnline') : t('checkout.startsOnPayment')}
        </p>
        {order.isError ? (
          <div className="mt-3">
            <Notice kind="error">
              {openId ? t('checkout.openOrder') : errorMessage(order.error, t)}
            </Notice>
            {openId ? (
              <LinkButton to={`/billing/invoices/${openId}`} variant="secondary" className="mt-2">
                {t('checkout.showOpen')}
              </LinkButton>
            ) : null}
          </div>
        ) : null}
        <Button type="submit" className="mt-4 w-full" busy={order.isPending}>
          {method === 'online' ? t('checkout.submitOnline') : t('checkout.submit')}
        </Button>
        <p className="mt-2 text-xs text-slate-700">{t('checkout.terms')}</p>
      </Card>
    </form>
  );
}

/** Order a plan on invoice (T-48): address, payment method, summary with VAT. */
export function Checkout() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const planParam = params.get('plan');
  const plan: PaidPlan = isPlan(planParam) ? planParam : 'business';
  const interval: Interval = params.get('interval') === 'year' ? 'year' : 'month';
  const billing = useQuery({ queryKey: ['billing'], queryFn: () => unwrap(api.GET('/billing')) });
  const address = useQuery({
    queryKey: ['billing', 'address'],
    queryFn: () => unwrap(api.GET('/billing/address')),
  });
  if (billing.isPending || address.isPending) return <Loading />;
  if (billing.isError || address.isError) {
    const error = billing.error ?? address.error;
    return (
      <LoadError
        message={errorMessage(error, t)}
        onRetry={() => void Promise.all([billing.refetch(), address.refetch()])}
      />
    );
  }
  return (
    <>
      <PageTitle>{t('checkout.title')}</PageTitle>
      <div className="flex flex-col gap-4">
        <BackButton to="/billing">{t('billing.title')}</BackButton>
        {!billing.data.live ? <Notice kind="warning">{t('checkout.demo')}</Notice> : null}
        {
          <>
            <div
              role="group"
              aria-label={t('billing.intervalLabel')}
              className="flex flex-wrap gap-1"
            >
              {(['starter', 'business', 'agency'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={plan === p}
                  onClick={() => setParams({ plan: p, interval }, { replace: true })}
                  className={`min-h-11 rounded-md border px-3 font-semibold ${plan === p ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
                >
                  {t(`admin.plans.${p}`)}
                </button>
              ))}
              <span className="mx-1 w-px self-stretch bg-slate-300" aria-hidden="true" />
              {(['month', 'year'] as const).map((i) => (
                <button
                  key={i}
                  type="button"
                  aria-pressed={interval === i}
                  onClick={() => setParams({ plan, interval: i }, { replace: true })}
                  className={`min-h-11 rounded-md border px-3 font-semibold ${interval === i ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
                >
                  {t(`billing.intervals.${i}`)}
                </button>
              ))}
            </div>
            <AddressForm
              initial={address.data.address}
              countries={address.data.countries}
              plan={plan}
              interval={interval}
              methods={billing.data.methods}
            />
          </>
        }
      </div>
    </>
  );
}
